import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Eye, FileText, MapPin, Receipt, Download, Mail, DollarSign, X } from 'lucide-react';
import RoadmapPrestataireModal from './RoadmapPrestataireModal';
import EmailSenderDialog from '@/components/EmailSenderDialog';
import DownloadOptionsDialog from '@/components/DownloadOptionsDialog';

const DevisList = () => {
  const navigate = useNavigate();
  const [devisList, setDevisList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [clientType, setClientType] = useState('all');
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('all');
  const [eventDate, setEventDate] = useState('');
  const [roadmapModalOpen, setRoadmapModalOpen] = useState(false);
  const [selectedDevisForRoadmap, setSelectedDevisForRoadmap] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [devisToSend, setDevisToSend] = useState(null);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [devisToDownload, setDevisToDownload] = useState(null);

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    loadDevis();
  }, [clientType, selectedClient, eventDate]);

  const loadClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`, { withCredentials: true });
      setClients(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des clients');
    }
  };

  const loadDevis = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (clientType !== 'all') {
        params.append('client_type', clientType);
      }
      if (selectedClient !== 'all') {
        params.append('client_id', selectedClient);
      }
      if (eventDate) {
        params.append('date_evenement_principal', eventDate);
      }
      const res = await axios.get(`${API}/devis`, { params, withCredentials: true });
      setDevisList(res.data);
    } catch (error) {
      console.error('Error loading devis:', error);
      toast.error('Erreur lors du chargement des devis');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce devis ?')) return;
    try {
      await axios.delete(`${API}/devis/${id}`, { withCredentials: true });
      toast.success('Devis supprimé');
      loadDevis();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSendEmailClick = (devis) => {
    setDevisToSend(devis);
    setEmailDialogOpen(true);
  };

  const handleSendEmail = async (senderEmail) => {
    try {
      await axios.post(
        `${API}/devis/${devisToSend.id}/send`,
        { sender_email: senderEmail },
        { withCredentials: true }
      );
      toast.success('Devis envoyé par email avec succès');
      setEmailDialogOpen(false);
      setDevisToSend(null);
      loadDevis();
    } catch (error) {
      console.error('Error sending devis:', error);
      toast.error('Erreur lors de l\'envoi de l\'email');
      throw error; // Re-throw pour que le dialogue gère le state
    }
  };

  const handleCreateRoadmap = (devisId) => {
    setSelectedDevisForRoadmap(devisId);
    setRoadmapModalOpen(true);
  };

  const handleRoadmapSuccess = () => {
    setRoadmapModalOpen(false);
    setSelectedDevisForRoadmap(null);
    navigate('/backoffice/roadmaps');
  };

  const handleCreateContrat = async (devisId) => {
    try {
      const res = await axios.post(`${API}/contrats/from-devis/${devisId}`, {}, { withCredentials: true });
      toast.success('Contrat créé');
      navigate(`/backoffice/contrats`);
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleChangeStatut = async (devisId, newStatut) => {
    try {
      await axios.patch(`${API}/devis/${devisId}/statut`, { statut: newStatut }, { withCredentials: true });
      toast.success('Statut mis à jour');
      loadDevis();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const handleToggleDocumentType = async (devisId, currentType) => {
    const newType = currentType === 'devis' ? 'facture' : 'devis';
    try {
      await axios.patch(`${API}/devis/${devisId}/toggle-document-type`, {}, { withCredentials: true });
      toast.success(`Document converti en ${newType}`);
      loadDevis();
    } catch (error) {
      toast.error('Erreur lors de la conversion');
    }
  };

  const handleDownloadDocument = (devis) => {
    // Open dialog to select RIB before download
    setDevisToDownload(devis);
    setDownloadDialogOpen(true);
  };

  const getStatusBadge = (statut) => {
    const styles = {
      brouillon: 'bg-stone-100 text-stone-600',
      envoye: 'bg-amber-100 text-amber-700',
      valide: 'bg-emerald-100 text-emerald-700',
      signe: 'bg-blue-100 text-blue-700',
      annule: 'bg-rose-100 text-rose-700',
    };
    const labels = { 
      brouillon: 'Brouillon', 
      envoye: 'Envoyé', 
      valide: 'Validé', 
      signe: 'Signé', 
      annule: 'Annulé' 
    };
    return <Badge className={styles[statut] || styles.brouillon}>{labels[statut] || statut}</Badge>;
  };

  const filteredDevis = devisList.filter(d =>
    `${d.numero} ${d.client_info?.nom} ${d.client_info?.prenom} ${d.event_info?.event_type}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-stone-800">Devis</h1>
          <p className="text-sm sm:text-base text-stone-600">Gérez vos devis</p>
        </div>
        <Link to="/backoffice/devis/new">
          <Button className="bg-amber-600 hover:bg-amber-700 w-full sm:w-auto" data-testid="btn-new-devis">
            <Plus className="h-4 w-4 mr-2" />
            Nouveau devis
          </Button>
        </Link>
      </div>

      {/* Search and Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-grow sm:flex-grow-0 sm:w-full sm:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input
              placeholder="Rechercher un devis..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-devis"
            />
          </div>
          <div className="flex-grow sm:flex-grow-0">
            <Select value={clientType} onValueChange={setClientType}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filtrer par type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="particulier">Particulier</SelectItem>
                <SelectItem value="partenaire">Partenaire</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-grow sm:flex-grow-0">
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger className="w-full sm:w-[240px]">
                <SelectValue placeholder="Filtrer par client" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les clients</SelectItem>
                {clients.map(client => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.prenom} {client.nom} ({client.type})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-grow sm:flex-grow-0">
            <div className="relative">
              <Input
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="w-full sm:w-[180px]"
              />
              {eventDate && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setEventDate('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-stone-700">N° Document</TableHead>
                <TableHead className="text-stone-700">Type</TableHead>
                <TableHead className="text-stone-700">Client</TableHead>
                <TableHead className="text-stone-700">Événement</TableHead>
                <TableHead className="text-stone-700">Date Évén.</TableHead>
                <TableHead className="text-stone-700">Total TTC</TableHead>
                <TableHead className="text-stone-700">Statut</TableHead>
                <TableHead className="text-right text-stone-700">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">Chargement...</TableCell>
                </TableRow>
              ) : filteredDevis.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-stone-500">Aucun devis trouvé</TableCell>
                </TableRow>
              ) : (
                filteredDevis.map((devis) => {
                  const docType = devis.document_type || 'devis';
                  return (
                    <TableRow key={devis.id} data-testid={`devis-row-${devis.id}`}>
                      <TableCell className="font-medium">{devis.numero}</TableCell>
                      <TableCell>
                        <Badge className={docType === 'facture' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                          {docType === 'facture' ? '💰 Facture' : '📄 Devis'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {devis.client_info?.prenom} {devis.client_info?.nom}
                      </TableCell>
                      <TableCell>
                        <span className="capitalize">{devis.event_info?.event_type?.replace('_', ' ')}</span>
                        <span className="text-sm text-stone-500 block">{devis.event_info?.vedettes}</span>
                      </TableCell>
                      <TableCell>
                        {devis.date_evenement_principal 
                          ? new Date(devis.date_evenement_principal).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell className="font-semibold">{devis.total_ttc?.toLocaleString()}€</TableCell>
                      <TableCell>
                        <select
                          value={devis.statut || 'brouillon'}
                          onChange={(e) => handleChangeStatut(devis.id, e.target.value)}
                          className="text-sm px-2 py-1 rounded border border-stone-300 bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                        >
                          <option value="brouillon">🟡 Brouillon</option>
                          <option value="envoye">📤 Envoyé</option>
                          <option value="valide">✅ Validé</option>
                          <option value="signe">✍️ Signé</option>
                          <option value="annule">❌ Annulé</option>
                        </select>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={`/devis/${devis.id}`} target="_blank" data-testid={`view-devis-${devis.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          {devis.evenement_id && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              asChild
                              title="Gérer les paiements"
                              className="text-green-600 hover:text-green-700"
                            >
                              <Link to={`/backoffice/finances/${devis.evenement_id}`}>
                                <DollarSign className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDownloadDocument(devis)} 
                            title="Télécharger le document (PDF)"
                            className="text-amber-600 hover:text-amber-700"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleSendEmailClick(devis)} 
                            title="Envoyer par email"
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={`/backoffice/devis/${devis.id}/edit`} data-testid={`edit-devis-${devis.id}`}>
                              <Edit className="h-4 w-4" />
                            </Link>
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleToggleDocumentType(devis.id, docType)} 
                            title={docType === 'devis' ? 'Transformer en facture' : 'Transformer en devis'}
                          >
                            <Receipt className={`h-4 w-4 ${docType === 'facture' ? 'text-blue-600' : 'text-green-600'}`} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleCreateContrat(devis.id)} title="Générer contrat">
                            <FileText className="h-4 w-4 text-blue-600" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleCreateRoadmap(devis.id)} title="Générer feuille de route">
                            <MapPin className="h-4 w-4 text-emerald-600" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(devis.id)}>
                            <Trash2 className="h-4 w-4 text-rose-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Modal pour assigner prestataires */}
      <RoadmapPrestataireModal
        open={roadmapModalOpen}
        onClose={() => {
          setRoadmapModalOpen(false);
          setSelectedDevisForRoadmap(null);
        }}
        devisId={selectedDevisForRoadmap}
        onSuccess={handleRoadmapSuccess}
      />

      {/* Email Sender Dialog */}
      {devisToSend && (
        <EmailSenderDialog
          open={emailDialogOpen}
          onOpenChange={setEmailDialogOpen}
          onSend={handleSendEmail}
          documentType={devisToSend.type === 'facture' ? 'la facture' : 'le devis'}
          recipientEmail={devisToSend.client_email}
          documentInfo={{ numero: devisToSend.numero }}
        />
      )}

      {/* Download Options Dialog */}
      {devisToDownload && (
        <DownloadOptionsDialog
          open={downloadDialogOpen}
          onOpenChange={setDownloadDialogOpen}
          documentType="devis"
          documentId={devisToDownload.id}
          documentName={`${devisToDownload.document_type === 'facture' ? 'Facture' : 'Devis'} ${devisToDownload.numero}.pdf`}
        />
      )}
    </div>
  );
};

export default DevisList;
